import { useReadContract, useReadContracts, useAccount } from 'wagmi'
import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { Address, formatUnits, zeroAddress } from 'viem'
import { useChainId } from 'wagmi'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { getChainConfigEntry } from '@/config/chain'
import { IsolatedPairOverview } from '../types'

import { Token } from '@/domain/types/Token'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { erc20AbiWithApproveFnMalformed as ERC20_ABI } from '@/features/actions/flavours/approve/logic/normalizeErc20Abi'
import BigNumber from 'bignumber.js'
import { MyWalletProps } from '../components/my-wallet/MyWallet'
import { BorrowEligibilityStatus } from '@/domain/market-info/reserve-status'
import { MAX_UINT160, QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'

import { OracleInfo, OracleInfoBase } from '@/domain/oracles/types'
import { ReserveOracleType } from '@/config/chain/types'
import { FRAX_RATE_CALCULATOR_ABI } from '@/config/abis/fraxRateCalculatorAbi.abi'
import { FRAX_ORACLE_ABI } from '@/config/abis/fraxOracleAbi.abi'
import { calculateIsolatedHealthFactor } from '@/domain/common/calculations/healthFactor'
import { calculateLiquidationPrice } from '@/domain/common/calculations/liquidation'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'

const SECONDS_PER_YEAR = 31_556_952

function getOracleProvider(oracleConfig: ReserveOracleType, oracleInfoBase: OracleInfoBase): OracleInfo {
  switch (oracleConfig.type) {
    case 'market-price': {
      return {
        ...oracleInfoBase,
        providedBy: oracleConfig.providedBy,
        type: oracleConfig.type,
      }
    }

    case 'redemption-rate': {
      return {
        ...oracleInfoBase,
        providedBy: oracleConfig.providedBy,
        type: oracleConfig.type,
      }
    }

    case 'fixed': {
      return {
        ...oracleInfoBase,
        type: oracleConfig.type,
      }
    }

    case 'underlying-asset': {
      return {
        ...oracleInfoBase,
        asset: oracleConfig.asset,
        type: oracleConfig.type,
      }
    }

    default:
      return {
        ...oracleInfoBase,
        type: 'unknown' as const,
      }
  }
}

export interface UseIsolatedDetailsResult {
  pairAddress: Address
  chainName: string
  chainId: number
  pairOverview: IsolatedPairOverview
  walletOverview: MyWalletProps
  chainMismatch: boolean
  isLoading: boolean
}

export interface UseIsolatedDetailsParams {
  chainId: number
  pairAddress: Address
}

export function useIsolatedPairDetails({ chainId, pairAddress }: UseIsolatedDetailsParams): UseIsolatedDetailsResult {
  const { meta: chainMeta } = getChainConfigEntry(chainId)
  const connectedChainId = useChainId()

  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: QUERY_REFETCH_INTERVAL })

  const chainMismatch = connectedChainId !== chainId
  const nativeAssetInfo = getNativeAssetInfo(chainId)
  const marketWalletInfo = useMarketWalletInfo({ chainId })
  const nativeToken = marketInfo.findOneReserveBySymbol(nativeAssetInfo.nativeAssetSymbol)
  const nativeBalance = marketWalletInfo.findWalletBalanceForSymbol(nativeAssetInfo.nativeAssetSymbol)

  const { data: accounting, isLoading: isAccountingLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'getPairAccounting',
  })

  const { data: rateInfo, isLoading: isRateInfoLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'previewAddInterest',
  })

  const { data: decimals, isLoading: isDecimalsLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'decimals',
  })

  const { data: collateralContract } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'collateralContract',
  })

  const { data: assetContract } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'asset',
  })

  const { data: collateralSymbol } = useReadContract({
    address: collateralContract || zeroAddress,
    abi: ERC20_ABI,
    functionName: 'symbol',
  })

  const { data: borrowSymbol } = useReadContract({
    address: assetContract || zeroAddress,
    abi: ERC20_ABI,
    functionName: 'symbol',
  })

  const { data: collateralDecimals } = useReadContract({
    address: collateralContract || zeroAddress,
    abi: ERC20_ABI,
    functionName: 'decimals',
  })

  const { data: borrowDecimals, isLoading: isBorrowDecimalsLoading } = useReadContract({
    address: assetContract || zeroAddress,
    abi: ERC20_ABI,
    functionName: 'decimals',
  })

  const { data: pairConfig, isLoading: isPairConfigLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'maxLTV',
  })

  const { data: borrowLimit, isLoading: isBorrowLimitLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'borrowLimit',
  })

  const { data: rateCalculatorAddress, isLoading: isRateCalculatorAddressLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'rateContract',
  })

  // Get rate parameters from rate calculator
  const { data: vertexUtilization, isLoading: isVertexUtilizationLoading } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'VERTEX_UTILIZATION',
  })

  const { data: vertexRatePercent } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'VERTEX_RATE_PERCENT',
  })

  const { data: minFullUtilRate } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'MIN_FULL_UTIL_RATE',
  })

  const { data: maxFullUtilRate } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'MAX_FULL_UTIL_RATE',
  })

  const { data: utilPrec, isLoading: isUtilPrecLoading } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'UTIL_PREC',
  })

  const { data: ratePrec, isLoading: isRatePrecLoading } = useReadContract({
    address: rateCalculatorAddress,
    abi: FRAX_RATE_CALCULATOR_ABI,
    functionName: 'RATE_PREC',
  })

  // Get oracle information and price
  const { data: exchangeRateInfo, isLoading: isExchangeRateInfoLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'exchangeRateInfo',
  })

  // Add a new contract read for constants
  const { data: constants, isLoading: isConstantsLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'getConstants',
  })

  const { address: userAddress } = useAccount()

  // Get user's snapshot (assetShares, borrowShares, collateralBalance)
  const { data: userSnapshot, isLoading: isUserSnapshotLoading } = useReadContract({
    address: pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'getUserSnapshot',
    args: [userAddress || zeroAddress],
  })

  // Get direct oracle prices for more accurate exchange rate calculation
  const { data: directOraclePrices, isLoading: isDirectOraclePricesLoading } = useReadContract({
    address: exchangeRateInfo?.[0],
    abi: FRAX_ORACLE_ABI,
    functionName: 'getPrices',
    query: {
      enabled: !!exchangeRateInfo && exchangeRateInfo[0] !== zeroAddress,
    },
  })

  // Get user's wallet balances
  const { data: userWalletData } = useReadContracts({
    contracts: [
      {
        address: (assetContract as Address) || zeroAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [userAddress || zeroAddress],
      },
      {
        address: (collateralContract as Address) || zeroAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [userAddress || zeroAddress],
      },
    ],
  })

  if (
    !accounting ||
    !rateInfo ||
    !decimals ||
    !collateralContract ||
    !assetContract ||
    !collateralSymbol ||
    !borrowSymbol ||
    !collateralDecimals ||
    !borrowDecimals ||
    !vertexUtilization ||
    !vertexRatePercent ||
    !minFullUtilRate ||
    !maxFullUtilRate ||
    !utilPrec ||
    !ratePrec ||
    !exchangeRateInfo ||
    !constants
  ) {
    const defaultToken = new Token({
      address: CheckedAddress(zeroAddress),
      symbol: TokenSymbol('-'),
      name: '',
      decimals: 18,
      unitPriceUsd: '0',
    })

    return {
      pairAddress,
      chainName: chainMeta.name,
      chainId,
      pairOverview: {
        normalizedExchangeRate: NormalizedUnitNumber(0),
        oraclesInfo: {
          type: 'market-price',
          providedBy: [],
          assetOracles: [],
        },
        chartProps: {
          optimalUtilizationRate: Percentage(50, true, true),
          utilizationRate: Percentage(0),
          variableRateSlope1: BigNumber(0),
          variableRateSlope2: BigNumber(0),
          baseVariableBorrowRate: BigNumber(0),
        },
        collateralAsset: new Token({
          address: CheckedAddress(zeroAddress),
          symbol: TokenSymbol('-'),
          name: '',
          decimals: 18,
          unitPriceUsd: '0',
        }),
        borrowAsset: new Token({
          address: CheckedAddress(zeroAddress),
          symbol: TokenSymbol('-'),
          name: '',
          decimals: 18,
          unitPriceUsd: '0',
        }),
        totalCollateral: NormalizedUnitNumber(0),
        totalSupplied: NormalizedUnitNumber(0),
        totalBorrowed: NormalizedUnitNumber(0),
        available: NormalizedUnitNumber(0),
        utilizationRate: Percentage(0, true, true),
        borrowAPY: Percentage(0, true, true),
        lendAPY: Percentage(0, true, true),
        marketSize: NormalizedUnitNumber(0),
        maxLtv: Percentage(0, true, true),
        supplyStatus: 'yes',
        borrowStatus: 'yes',
        oracleAddress: CheckedAddress(zeroAddress),
        exchangeRate: NormalizedUnitNumber(0),
        borrowLimit: NormalizedUnitNumber(0),
      },
      chainMismatch,
      walletOverview: {
        pairAddress,
        guestMode: !userAddress,
        token: defaultToken,
        tokenBalance: NormalizedUnitNumber(0),
        healthFactor: BigNumber(0),
        nativeToken: { token: nativeToken.token, available: nativeBalance },
        liquidationDetails: {
          liquidationPrice: NormalizedUnitNumber(0),
          tokenWithPrice: {
            priceInUSD: NormalizedUnitNumber(0),
            symbol: defaultToken.symbol,
          },
        },
        deposit: {
          available: NormalizedUnitNumber(0),
          token: defaultToken,
        },
        borrow: {
          available: NormalizedUnitNumber(0),
          poolAvailable: NormalizedUnitNumber(0),
          token: defaultToken,
          eligibility: 'no' as BorrowEligibilityStatus,
        },
        depositBorrowAsset: {
          available: NormalizedUnitNumber(0),
          token: defaultToken,
        },
        withdraw: {
          available: NormalizedUnitNumber(0),
          total: NormalizedUnitNumber(0),
          token: defaultToken,
        },
        withdrawBorrowAsset: {
          available: NormalizedUnitNumber(0),
          token: defaultToken,
        },
        repay: {
          available: NormalizedUnitNumber(0),
          token: defaultToken,
        },
      },
      isLoading:
        isAccountingLoading ||
        isRateInfoLoading ||
        isDecimalsLoading ||
        isExchangeRateInfoLoading ||
        isConstantsLoading ||
        isUserSnapshotLoading ||
        isVertexUtilizationLoading ||
        isUtilPrecLoading ||
        isRatePrecLoading ||
        isPairConfigLoading ||
        isRateCalculatorAddressLoading ||
        isBorrowDecimalsLoading ||
        isBorrowLimitLoading ||
        false,
    }
  }

  const [totalAssetAmount, totalAssetShares, totalBorrow, totalBorrowShares, totalCollateral] = accounting
  const [, , , { feeToProtocolRate, ratePerSec }, , ,] = rateInfo

  const available = totalAssetAmount - totalBorrow
  const utilization = totalAssetAmount > 0n ? Number((totalBorrowShares * 1000000n) / totalAssetShares) / 10000 : 0

  // Calculate APY using rate info
  const ratePerSecNum = Number(ratePerSec) / 1e18
  // feeToProtocolRate is 1e5 precision
  // see: https://github.com/FraxFinance/fraxlend-audit/blob/76c537d429ea4ac9bed2b13fc2cca90cccb2791a/src/contracts/FraxlendPairCore.sol#L103
  const feeToProtocolRateNum = Number(feeToProtocolRate) / 1e5

  // Compute Borrow APY using continuous compounding formula
  const borrowAPY = ratePerSecNum > 0 ? (Math.exp(ratePerSecNum * SECONDS_PER_YEAR) - 1) * 100 : 0

  const lendAPY = borrowAPY * (utilization / 100) * (1 - feeToProtocolRateNum)

  const utilPrecBN = new BigNumber(utilPrec.toString())
  const ratePrecBN = new BigNumber(ratePrec.toString())
  const vertexUtilizationBN = new BigNumber(vertexUtilization.toString())
  const vertexRatePercentBN = new BigNumber(vertexRatePercent.toString())
  const maxFullUtilRateBN = new BigNumber(maxFullUtilRate.toString())

  const optimalUtilizationRate = vertexUtilizationBN.div(utilPrecBN)

  const variableRateSlope1 = vertexRatePercentBN.times(ratePrecBN).div(utilPrecBN).div(1e5)
  const variableRateSlope2 = maxFullUtilRateBN.times(ratePrecBN).div(utilPrecBN.minus(vertexUtilizationBN))

  // Extract constants from the getConstants call
  const [
    _LTV_PRECISION,
    _LIQ_PRECISION,
    _UTIL_PREC,
    _FEE_PRECISION,
    _EXCHANGE_PRECISION,
    _DEVIATION_PRECISION,
    _RATE_PRECISION,
    _MAX_PROTOCOL_FEE,
  ] = constants

  // Extract oracle info
  const [oracleAddress, _maxOracleDeviation, _oracleLastTimestamp, lowExchangeRate, _highExchangeRate] =
    exchangeRateInfo

  // Use oracle prices directly if available, otherwise fall back to the old calculation
  const currentExchangeRate =
    (Number(directOraclePrices ? Number(directOraclePrices[1]) : Number(lowExchangeRate)) /
      Number(_EXCHANGE_PRECISION)) *
    10 ** (borrowDecimals - collateralDecimals)

  const userAssetShares = userSnapshot?.[0] || 0n
  const userBorrowShares = userSnapshot?.[1] || 0n
  const userCollateralAmount = userSnapshot?.[2] || 0n

  const userAssetAmount =
    userAssetShares > 0n && totalAssetShares > 0n ? (userAssetShares * totalAssetAmount) / totalAssetShares : 0n

  const userCollateralWalletBalance = userWalletData?.[1]?.result || 0n

  // Calculate user's borrowed amount
  const userBorrowAmount =
    userBorrowShares > 0n && totalBorrowShares > 0n ? (userBorrowShares * totalBorrow) / totalBorrowShares : 0n

  // Calculate max borrowable based on current collateral and maxLTV with safety margin
  const maxBorrowableAmount =
    pairConfig && userCollateralAmount && borrowDecimals && collateralDecimals
      ? BigInt(
          Math.floor(
            (Number(userCollateralAmount) * (1 / currentExchangeRate) * (Number(pairConfig) / 100000) * 0.995) /
              10 ** (collateralDecimals - borrowDecimals),
          ),
        )
      : 0n

  // Ensure remaining capacity respects both the max borrowable amount and doesn't exceed pool available
  const remainingBorrowCapacity = userBorrowAmount < maxBorrowableAmount ? maxBorrowableAmount - userBorrowAmount : 0n

  function calculateRequiredCollateral(): number {
    if (userBorrowAmount === 0n || !borrowDecimals || !pairConfig) return 0

    const borrowedAmount = new BigNumber(formatUnits(userBorrowAmount, borrowDecimals))
    const maxLtvRatio = new BigNumber(pairConfig.toString()).dividedBy(100000)
    const requiredCollateral = borrowedAmount.dividedBy(maxLtvRatio).times(currentExchangeRate)

    return requiredCollateral.toNumber()
  }

  const collateralReserveToken = marketInfo.reserves.find((r) => r.token.address === collateralContract)
  const borrowReserveToken = marketInfo.reserves.find((r) => r.token.address === assetContract)

  let collateralPrice = collateralReserveToken?.priceInUSD.toString() || '0'

  let borrowPrice = borrowReserveToken?.priceInUSD.toString() || '0'

  if (collateralPrice === '0' && borrowPrice !== '0') {
    collateralPrice = (Number(borrowPrice) * Number(1 / currentExchangeRate)).toString()
  } else if (borrowPrice === '0' && collateralPrice !== '0') {
    borrowPrice = (Number(collateralPrice) * currentExchangeRate).toString()
  } else if (collateralPrice === '0' && borrowPrice === '0' && directOraclePrices) {
    collateralPrice = '0'
    borrowPrice = '0'
  }

  const collateralOracleConfig = collateralReserveToken
    ? getChainConfigEntry(marketInfo.chainId).markets?.oracles[collateralReserveToken?.token.symbol]
    : undefined
  const borrowOracleConfig = borrowReserveToken
    ? getChainConfigEntry(marketInfo.chainId).markets?.oracles[borrowReserveToken?.token.symbol]
    : undefined
  const pairOracleConfig = getChainConfigEntry(marketInfo.chainId).isolated?.oracles[CheckedAddress(pairAddress)]

  // Calculate chartProps with proper FraxLend parameters
  const chartProps = {
    optimalUtilizationRate: Percentage(optimalUtilizationRate.toNumber(), true, true),
    utilizationRate: Percentage(utilization / 100, true, true),
    variableRateSlope1,
    variableRateSlope2,
    // Set baseVariableBorrowRate to match the real rate
    baseVariableBorrowRate: new BigNumber(0),
    // Add FraxLend-specific parameters
    isExponentialRate: true,
    zeroUtilRate: new BigNumber(ratePerSecNum),
    minFullUtilRate: new BigNumber(Number(minFullUtilRate) / 1e18),
    maxFullUtilRate: new BigNumber(Number(maxFullUtilRate) / 1e18),
  }

  const pairOverview: IsolatedPairOverview = {
    oraclesInfo: {
      type: pairOracleConfig?.type || 'market-price',
      providedBy: pairOracleConfig?.providedBy || [],
      assetOracles: [],
    },
    collateralAsset: getDisplayToken(
      marketInfo.chainId,
      new Token({
        address: CheckedAddress(collateralContract),
        symbol: TokenSymbol(collateralSymbol || '-'),
        name: collateralSymbol,
        decimals: collateralDecimals,
        unitPriceUsd: collateralPrice,
      }),
    ),
    borrowAsset: getDisplayToken(
      marketInfo.chainId,
      new Token({
        address: CheckedAddress(assetContract),
        symbol: TokenSymbol(borrowSymbol || '-'),
        name: borrowSymbol,
        decimals: borrowDecimals,
        unitPriceUsd: borrowPrice,
      }),
    ),
    oracleAddress: CheckedAddress(oracleAddress),
    normalizedExchangeRate: NormalizedUnitNumber(Number(1 / currentExchangeRate)),
    exchangeRate: NormalizedUnitNumber(Number(currentExchangeRate)),
    chartProps,
    totalCollateral: NormalizedUnitNumber(formatUnits(totalCollateral, collateralDecimals)),
    totalSupplied: NormalizedUnitNumber(formatUnits(totalAssetAmount, decimals)),
    totalBorrowed: NormalizedUnitNumber(formatUnits(totalBorrow, decimals)),
    available: NormalizedUnitNumber(formatUnits(available, decimals)),
    utilizationRate: Percentage(utilization / 100, true, true),
    borrowAPY: Percentage(borrowAPY / 100, true, true),
    lendAPY: Percentage(lendAPY / 100, true, true),
    marketSize: NormalizedUnitNumber(formatUnits(totalAssetAmount, decimals)),
    maxLtv: pairConfig ? Percentage(Number(pairConfig) / 100000, true, true) : Percentage(0, true, true),
    // if set to MAX_UINT160, there is no borrow limit
    borrowLimit:
      borrowLimit && borrowLimit !== MAX_UINT160
        ? NormalizedUnitNumber(formatUnits(borrowLimit, borrowDecimals))
        : NormalizedUnitNumber(0),
    supplyStatus: 'yes',
    borrowStatus: 'yes',
  }

  if (borrowReserveToken && borrowOracleConfig) {
    const borrowOracle: OracleInfoBase = {
      token: borrowReserveToken?.token,
      price: NormalizedUnitNumber(borrowReserveToken?.priceInUSD),
      priceOracleAddress: borrowReserveToken?.priceOracle,
      chainId: marketInfo.chainId,
    }

    const oracleInfo = getOracleProvider(borrowOracleConfig, borrowOracle)

    if (oracleInfo) {
      pairOverview.oraclesInfo.assetOracles.push(oracleInfo)
    }
  }

  if (collateralReserveToken && collateralOracleConfig) {
    const collateralOracle: OracleInfoBase = {
      token: collateralReserveToken?.token,
      price: NormalizedUnitNumber(collateralReserveToken?.priceInUSD),
      priceOracleAddress: collateralReserveToken?.priceOracle,
      chainId: marketInfo.chainId,
    }

    const oracleInfo = getOracleProvider(collateralOracleConfig, collateralOracle)
    if (oracleInfo) {
      pairOverview.oraclesInfo.assetOracles.push(oracleInfo)
    }
  }

  const borrow = {
    available: NormalizedUnitNumber(formatUnits(remainingBorrowCapacity, decimals)),
    poolAvailable: NormalizedUnitNumber(formatUnits(available, decimals)),
    token: new Token({
      address: CheckedAddress(assetContract),
      symbol: TokenSymbol(borrowSymbol || '-'),
      name: borrowSymbol || '',
      decimals: borrowDecimals,
      unitPriceUsd: borrowPrice,
    }),
    eligibility: (userCollateralAmount > 0n ? 'yes' : 'no') as BorrowEligibilityStatus,
  }

  // Calculate current LTV with proper zero checks
  const collateralValueUSD = NormalizedUnitNumber(formatUnits(userCollateralAmount, collateralDecimals)).multipliedBy(
    collateralPrice,
  )
  const borrowValueUSD = NormalizedUnitNumber(formatUnits(userBorrowAmount, borrowDecimals)).multipliedBy(borrowPrice)

  const currentLtv =
    collateralValueUSD.isZero() || borrowValueUSD.isZero()
      ? Percentage(0)
      : Percentage(borrowValueUSD.dividedBy(collateralValueUSD), true)

  const walletOverview: MyWalletProps = {
    guestMode: !userAddress,
    pairAddress,
    healthFactor: calculateIsolatedHealthFactor(
      NormalizedUnitNumber(formatUnits(userCollateralAmount, collateralDecimals)),
      pairOverview.normalizedExchangeRate,
      NormalizedUnitNumber(formatUnits(userBorrowAmount, borrowDecimals)),
      pairOverview.maxLtv,
    ),
    liquidationDetails: {
      liquidationPrice: calculateLiquidationPrice(
        NormalizedUnitNumber(collateralPrice),
        currentLtv,
        pairOverview.maxLtv,
      ),
      tokenWithPrice: {
        priceInUSD: NormalizedUnitNumber(collateralPrice),
        symbol: TokenSymbol(collateralSymbol || '-'),
      },
    },
    token: new Token({
      address: CheckedAddress(collateralContract),
      symbol: TokenSymbol(collateralSymbol || '-'),
      name: collateralSymbol || '',
      decimals: collateralDecimals,
      unitPriceUsd: collateralPrice,
    }),
    tokenBalance: NormalizedUnitNumber(formatUnits(userCollateralWalletBalance, collateralDecimals)),
    nativeToken: { token: nativeToken.token, available: nativeBalance },
    deposit: {
      available: NormalizedUnitNumber(formatUnits(userCollateralWalletBalance, collateralDecimals)),
      token: new Token({
        address: CheckedAddress(collateralContract),
        symbol: TokenSymbol(collateralSymbol || '-'),
        name: collateralSymbol || '',
        decimals: collateralDecimals,
        unitPriceUsd: collateralPrice,
      }),
    },
    depositBorrowAsset: {
      available: NormalizedUnitNumber(formatUnits(userWalletData?.[0]?.result || 0n, borrowDecimals)),
      token: new Token({
        address: CheckedAddress(assetContract),
        symbol: TokenSymbol(borrowSymbol || '-'),
        name: borrowSymbol || '',
        decimals: borrowDecimals,
        unitPriceUsd: borrowPrice,
      }),
    },
    withdraw: {
      total: NormalizedUnitNumber(formatUnits(userCollateralAmount, collateralDecimals)),
      available: NormalizedUnitNumber(
        Math.max(Number(formatUnits(userCollateralAmount, collateralDecimals)) - calculateRequiredCollateral(), 0),
      ),
      token: new Token({
        address: CheckedAddress(collateralContract),
        symbol: TokenSymbol(collateralSymbol || '-'),
        name: collateralSymbol || '',
        decimals: collateralDecimals,
        unitPriceUsd: collateralPrice,
      }),
    },
    withdrawBorrowAsset: {
      available: NormalizedUnitNumber(formatUnits(userAssetAmount, decimals)),
      token: new Token({
        address: CheckedAddress(assetContract),
        symbol: TokenSymbol(borrowSymbol || '-'),
        name: borrowSymbol || '',
        decimals: borrowDecimals,
        unitPriceUsd: borrowPrice,
      }),
    },
    borrow,
    repay: {
      available: NormalizedUnitNumber(formatUnits(userBorrowAmount, borrowDecimals)),
      token: new Token({
        address: CheckedAddress(assetContract),
        symbol: TokenSymbol(borrowSymbol || '-'),
        name: borrowSymbol || '',
        decimals: borrowDecimals,
        unitPriceUsd: borrowPrice,
      }),
    },
  }

  return {
    pairAddress,
    chainName: chainMeta.name,
    chainId,
    pairOverview,
    walletOverview,
    chainMismatch,
    isLoading:
      isAccountingLoading ||
      isRateInfoLoading ||
      isDecimalsLoading ||
      isExchangeRateInfoLoading ||
      isConstantsLoading ||
      isUserSnapshotLoading ||
      isVertexUtilizationLoading ||
      isUtilPrecLoading ||
      isRatePrecLoading ||
      isPairConfigLoading ||
      isRateCalculatorAddressLoading ||
      isBorrowDecimalsLoading ||
      isBorrowLimitLoading ||
      isDirectOraclePricesLoading,
  }
}
